import type { ReactElement } from 'react';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material';
import { TaxReliefElegibleType } from 'further-types/exit';

const TooltipWrapper = styled('span')(() => ({
  display: 'inline-block',
  cursor: 'not-allowed',
}));

export type TaxReliefButtonRendererProps = {
  disableEdit: boolean;
  isEntireFieldDisabled: boolean;
  isYesDisabled: boolean;
  isNoDisabled: boolean;
  isPartialDisabled: boolean;
  originalTaxReliefElegible?: TaxReliefElegibleType;
};

/**
 * Renders a tax relief radio button with appropriate tooltip and disabled state.
 * Returns the complete JSX with Tooltip wrapper if needed.
 */
export function renderTaxReliefButton(
  option: TaxReliefElegibleType,
  button: ReactElement,
  props: TaxReliefButtonRendererProps,
): ReactElement {
  const {
    isEntireFieldDisabled,
    isYesDisabled,
    isNoDisabled,
    isPartialDisabled,
  } = props;

  let tooltipText: string | null = null;

  // When entire field is disabled, all options get the same tooltip
  if (isEntireFieldDisabled) {
    tooltipText =
      "This exit's S/EIS eligible status has already been set. If there is no taxable value increase, an edit must follow the original exit's tax status.";
  } else if (isNoDisabled && isPartialDisabled) {
    // When both No and Partial are disabled, original was No
    // Show same tooltip for all disabled options
    tooltipText =
      'This exit was originally non-S/EIS qualifying. To change this, the original exit must be corrected.';
  } else if (isYesDisabled && !isNoDisabled && !isPartialDisabled) {
    // When only Yes is disabled, original was Partial
    // Show tooltip only on the Yes button
    if (option === TaxReliefElegibleType.Yes) {
      tooltipText =
        'This exit was originally partially non-S/EIS qualifying. To change this, the original exit must be corrected.';
    }
  }

  if (tooltipText) {
    return (
      <Tooltip title={tooltipText} arrow disableInteractive={false}>
        <TooltipWrapper>{button}</TooltipWrapper>
      </Tooltip>
    );
  }

  return button;
}
